import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const VehiculeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vehicule, setVehicule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
        fetchVehicule();
    }, [id]);

    const fetchVehicule = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/vehicules/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVehicule(response.data);
            setError(null);
        } catch (err) {
            setError('Erreur lors du chargement du véhicule');
            console.error('Erreur API:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            await axios.delete(`${API_URL}/vehicules/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate('/vehicules');
        } catch (err) {
            alert('Erreur lors de la suppression du véhicule');
            console.error(err);
        } finally {
            setDeleteLoading(false);
            setShowDeleteConfirm(false);
        }
    };

    const canManage = user && ['admin', 'validateur', 'chef_equipe'].includes(user.role);
    const canCreateDemande = user && ['client', 'mecanicien', 'validateur'].includes(user.role);

    if (loading) {
        return (
            <div className="min-h-screen  py-8">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="animate-pulse space-y-6">
                        <div className="h-8 bg-gray-200 rounded w-64"></div>
                        <div className="bg-white rounded-lg border p-6">
                            <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i}>
                                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                                        <div className="h-5 bg-gray-200 rounded w-48"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="max-w-md w-full">
                    <div className="bg-white p-6 rounded-lg border">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">Erreur</h2>
                        <p className="text-gray-600 text-center mb-6">{error}</p>
                        <div className="text-center">
                            <Link
                                to="/vehicules"
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Retour à la liste
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!vehicule) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="max-w-md w-full">
                    <div className="bg-white p-6 rounded-lg border">
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mx-auto mb-4">
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">Véhicule non trouvé</h2>
                        <p className="text-gray-600 text-center mb-6">Le véhicule demandé n'existe pas.</p>
                        <div className="text-center">
                            <Link
                                to="/vehicules"
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Retour aux véhicules
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link 
                            to="/vehicules" 
                            className="inline-flex items-center text-gray-600 hover:text-gray-900 text-sm"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Retour aux véhicules
                        </Link>
                    </div>
                    
                    <div className="flex items-start justify-between mb-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{vehicule.numero}</h1>
                            {vehicule.marque && vehicule.modele && (
                                <p className="text-gray-600 mt-1">{vehicule.marque} {vehicule.modele}</p>
                            )}
                        </div>
                        {canManage && (
                            <div className="flex space-x-2">
                                <Link
                                    to={`/vehicules/${id}/edit`}
                                    className="inline-flex items-center px-3 py-2 bg-white border text-gray-700 rounded hover:"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Modifier
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* Informations principales */}
                <div className="bg-white rounded-lg border mb-6">
                    <div className="p-6 border-b">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <h2 className="font-semibold text-gray-900">Informations du véhicule</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Colonne gauche */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm text-gray-500 mb-1">Numéro d'immatriculation</label>
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                        <span className="font-medium">{vehicule.numero}</span>
                                    </div>
                                </div>
                                
                                {vehicule.marque && (
                                    <div>
                                        <label className="block text-sm text-gray-500 mb-1">Marque</label>
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            <span className="font-medium">{vehicule.marque}</span>
                                        </div>
                                    </div>
                                )}
                                
                                <div>
                                    <label className="block text-sm text-gray-500 mb-1">Kilométrage actuel</label>
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                        <span className="font-medium">{vehicule.km_actuel?.toLocaleString() || '0'} km</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Colonne droite */}
                            <div className="space-y-4">
                                {vehicule.modele && (
                                    <div>
                                        <label className="block text-sm text-gray-500 mb-1">Modèle</label>
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                            <span className="font-medium">{vehicule.modele}</span>
                                        </div>
                                    </div>
                                )}
                                
                                <div>
                                    <label className="block text-sm text-gray-500 mb-1">Date d'enregistrement</label>
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="font-medium">{new Date(vehicule.created_at).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-sm text-gray-500 mb-1">Dernière mise à jour</label>
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-medium">{new Date(vehicule.updated_at).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Conducteur affecté */}
                    {(vehicule.user || vehicule.utilisateur) && (
                        <div className="p-6 border-t">
                            <div className="flex items-center mb-4">
                                <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-gray-900">Conducteur affecté</h3>
                            </div>
                            <div className="flex items-center p-4  rounded">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                                    <span className="text-blue-600 font-medium text-sm">
                                        {(vehicule.user?.name?.[0] || vehicule.utilisateur?.name?.[0] || 'U').toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900">
                                        {vehicule.user?.name || vehicule.utilisateur?.name || 'Utilisateur'}
                                    </div>
                                    {vehicule.user?.email && (
                                        <div className="text-sm text-gray-600">{vehicule.user.email}</div>
                                    )}
                                    {vehicule.user?.role && (
                                        <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                                            {vehicule.user.role}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="bg-white rounded-lg border p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Actions</h3>
                    
                    <div className="space-y-3">
                        {canCreateDemande && (
                            <Link
                                to={`/demandes-reparation/nouvelle?vehicule_id=${id}`}
                                className="flex items-center justify-between p-4 border rounded hover:bg-blue-50 hover:border-blue-200 transition-colors"
                            >
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center mr-3">
                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900">Nouvelle demande</div>
                                        <div className="text-sm text-gray-600">Créer une demande de réparation</div>
                                    </div>
                                </div>
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        )}
                        
                        {canManage && (
                            <>
                                <Link
                                    to={`/vehicules/${id}/edit`}
                                    className="flex items-center justify-between p-4 border rounded hover: transition-colors"
                                >
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center mr-3">
                                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Modifier le véhicule</div>
                                            <div className="text-sm text-gray-600">Éditer les informations</div>
                                        </div>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                                
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="w-full flex items-center justify-between p-4 border border-red-200 rounded hover:bg-red-50 text-left transition-colors"
                                >
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center mr-3">
                                            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Supprimer le véhicule</div>
                                            <div className="text-sm text-gray-600">Supprimer définitivement</div>
                                        </div>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de confirmation de suppression */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                            Confirmer la suppression
                        </h3>
                        <p className="text-gray-600 text-center mb-6">
                            Êtes-vous sûr de vouloir supprimer ce véhicule ? Cette action est irréversible.
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="flex-1 py-2 border rounded text-gray-700 hover:"
                                disabled={deleteLoading}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="flex-1 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                                {deleteLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    </div>
                                ) : (
                                    'Supprimer'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VehiculeDetails;